import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { inject, observer } from 'mobx-react';
import TweenMax from 'TweenMax';
import TimelineMax from 'TimelineMax';
import debounce from 'lodash/debounce';

import { hoverDefault, toVw, transitionDefault } from '../../../utils/styles';
import mq from '@/utils/media-queries';

const Styled = styled.div`
  position: relative;
  cursor: pointer;
  will-change: transform;
  ${hoverDefault};

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: -10px;
    right: -10px;
    bottom: -10px;
    left: -10px;
  }

  ${props =>
    props.disabled &&
    css`
      pointer-events: none;
    `};
`;

const StyledLine = styled.div`
  height: 4px;
  width: 36px;
  background-color: ${props => props.theme.colorText};
  will-change: transform;
  transform: scaleX(1);
  ${transitionDefault};

  &:first-child {
    transform-origin: left center;
    margin-bottom: 14px;
  }

  &:last-child {
    transform-origin: right center;
  }

  ${mq.mobile(css`
    width: 40px;

    &:first-child {
      margin-bottom: 16px;
    }
  `)};

  ${mq.mobile375(css`
    height: 2px;
    width: 28px;

    &:first-child {
      margin-bottom: 10px;
    }
  `)};
`;

class Burger extends React.Component {
  static propTypes = {
    onClick: PropTypes.func,
    isOnDark: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    menuVisible: PropTypes.bool.isRequired,
  };

  state = {
    disabled: false,
  };

  componentDidMount() {
    this.updateValues();
    window.addEventListener('resize', this.resizeHandler);
    this.containerNode.addEventListener('mouseenter', this.mouseEnterHandler);
    this.containerNode.addEventListener('mouseleave', this.mouseLeaveHandler);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeHandler);
    this.containerNode.removeEventListener('mouseenter', this.mouseEnterHandler);
    this.containerNode.removeEventListener('mouseleave', this.mouseLeaveHandler);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.loading !== this.props.loading) {
      if (nextProps.loading) {
        this.startProgressAnimation();
      }
    }

    if (nextProps.menuVisible !== this.props.menuVisible && !this.props.loading) {
      if (nextProps.menuVisible) {
        TweenMax.to(this.line1, 0.3, { y: this.yOffset });
        TweenMax.to(this.line2, 0.3, { y: -this.yOffset });
      } else if (!nextProps.loading) {
        TweenMax.to(this.line1, 0.3, { y: 0 });
        TweenMax.to(this.line2, 0.3, { y: 0 });
      }
    }
  }

  resizeHandler = debounce(() => {
    this.updateValues();
  }, 200);

  mouseEnterHandler = () => {
    if (!this.props.menuVisible && !this.props.loading) {
      TweenMax.to(this.line1, 0.3, { y: -5 });
      TweenMax.to(this.line2, 0.3, { y: 5 });
    }
  };

  mouseLeaveHandler = () => {
    if (!this.props.menuVisible && !this.props.loading) {
      TweenMax.to(this.line1, 0.3, { y: 0 });
      TweenMax.to(this.line2, 0.3, { y: 0 });
    }
  };

  startProgressAnimation() {
    this.setState({ disabled: true });

    const tl = new TimelineMax();

    tl.add(TweenMax.to(this.line1, 0.2, { y: this.yOffset }));
    tl.add(TweenMax.to(this.line2, 0.2, { y: -this.yOffset }));

    const rotate = () => {
      TweenMax.fromTo(
        this.containerNode,
        1,
        {
          rotation: 0,
        },
        {
          rotation: 360,
          ease: Power1.easeOut,
          onComplete: () => {
            if (this.props.loading) {
              setTimeout(rotate, 150);
            } else {
              const y = this.props.menuVisible ? this.yOffset : 0;
              TweenMax.to(this.line1, 0.2, { y });
              TweenMax.to(this.line2, 0.2, {
                y: -y,
                onComplete: () => {
                  this.setState({ disabled: false });
                },
              });
            }
          },
        }
      );
    };

    tl.add(rotate);
  }

  updateValues() {
    this.containerHeight = this.containerNode.offsetHeight;
    this.lineHeight = this.line1.offsetHeight;
    this.yOffset = (this.containerHeight - this.lineHeight) / 2;
  }

  render() {
    const { className, onClick, loading } = this.props;

    return (
      <Styled
        className={className}
        onClick={onClick}
        data-test="burger"
        data-link
        innerRef={node => (this.containerNode = node)}
        loading={loading}
        disabled={this.state.disabled}
      >
        <StyledLine innerRef={node => (this.line1 = node)} />
        <StyledLine innerRef={node => (this.line2 = node)} />
      </Styled>
    );
  }
}

export default inject(stores => ({
  progressStore: stores.store.progress,
  menuStore: stores.store.menuStore,
}))(
  observer(({ progressStore: { loading }, menuStore, ...rest }) => (
    <Burger loading={loading} menuVisible={menuStore.visible} {...rest} />
  ))
);



// WEBPACK FOOTER //
// ./src/components/Header/components/Burger.js